import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useDraggable } from '@/contexts/DraggableContext';
import { useParams } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  FileText,
  Database,
  Search,
  Download,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  Settings,
  BarChart3,
  Bug,
  Zap
} from 'lucide-react';
import { DMV_CATEGORIES } from './DMVCategories';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';

// Import existing BACPAC component logic
import BacpacAssessmentReport from './BacpacAssessmentReport';

const DiagnosticsPage = () => {

  const params = useParams();
  const clientCode = params.clientCode || 'costco';
  const [activeTab, setActiveTab] = useState('bacpac');
  const { tabOrders, setTabOrder } = useDraggable();
  const tabKey = `diagnostics-tabs`;
  // Mock data for other diagnostic types
  const diagnosticStats = {
    bacpac: { total: 42, critical: 8, high: 15, medium: 12, low: 7 },
    dacpac: { total: 28, critical: 3, high: 9, medium: 11, low: 5 },
    glennBerry: { total: 156, critical: 12, high: 34, medium: 67, low: 43 },
    performance: { total: 23, critical: 5, high: 8, medium: 7, low: 3 },
    security: { total: 19, critical: 2, high: 6, medium: 8, low: 3 }
  };

  const defaultTabs = [
    {
      key: 'bacpac',
      icon: FileText,
      title: 'BACPAC',
      stats: diagnosticStats.bacpac,
    },
    {
      key: 'dacpac',
      icon: Database,
      title: 'DACPAC',
      stats: diagnosticStats.dacpac,
    },
    {
      key: 'querystore',
      icon: BarChart3,
      title: 'Query Store',
      stats: { total: 0, critical: 0 },
    },
    {
      key: 'dmv',
      icon: Search,
      title: 'DMV',
      stats: { total: 0, critical: 0 },
    },
  ];
  const [tabs, setTabs] = useState(defaultTabs);

  // Load tab order from context
  useEffect(() => {
    const order = tabOrders[tabKey];
    if (order) {
      setTabs(order.map(k => defaultTabs.find(t => t.key === k)).filter(Boolean) as typeof defaultTabs);
    }
  }, [tabOrders]);

  // Handle drag end
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const newTabs = Array.from(tabs);
    const [removed] = newTabs.splice(result.source.index, 1);
    newTabs.splice(result.destination.index, 0, removed);
    setTabs(newTabs);
    setTabOrder(tabKey, newTabs.map(t => t.key));
  };



  const TabHeader = ({ icon: Icon, title, stats, tabKey, isActive }: {
    icon: any;
    title: string;
    stats: any;
    tabKey: string;
    isActive: boolean;
  }) => (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${isActive
        ? 'bg-shiftleft-100 text-shiftleft-800 border border-shiftleft-200'
        : 'hover:bg-gray-50 text-gray-600'
      }`}>
      <Icon className="w-4 h-4" />
      <span className="font-medium">{title}</span>
      <Badge variant={isActive ? "default" : "secondary"} className="ml-1">
        {stats.total}
      </Badge>
      {stats.critical > 0 && (
        <Badge variant="destructive" className="ml-1 bg-red-600">
          {stats.critical}
        </Badge>
      )}
    </div>
  );

  const DacpacFindings = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">DACPAC Assessment Report</h2>
          <p className="text-gray-600 mt-1">Database application package deployment analysis for {clientCode.toUpperCase()}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Issues</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{diagnosticStats.dacpac.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-red-600">Critical</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{diagnosticStats.dacpac.critical}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-orange-600">High</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{diagnosticStats.dacpac.high}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-yellow-600">Medium</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{diagnosticStats.dacpac.medium}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>DACPAC Deployment Issues</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>DACPAC analysis implementation coming soon...</p>
            <p className="text-sm mt-2">Will analyze schema drift, deployment conflicts, and version compatibility</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const GlennBerryFindings = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Glenn Berry Diagnostic Queries</h2>
          <p className="text-gray-600 mt-1">Comprehensive SQL Server performance and health analysis for {clientCode.toUpperCase()}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Checks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{diagnosticStats.glennBerry.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-red-600">Critical</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{diagnosticStats.glennBerry.critical}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-orange-600">High</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{diagnosticStats.glennBerry.high}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-yellow-600">Medium</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{diagnosticStats.glennBerry.medium}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-600">Informational</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{diagnosticStats.glennBerry.low}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Performance Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm">CPU Usage Patterns</span>
                <Badge variant="secondary">Analyzed</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Wait Statistics</span>
                <Badge variant="destructive">Issues Found</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Index Usage</span>
                <Badge variant="secondary">Optimizable</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuration Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm">Memory Configuration</span>
                <Badge variant="destructive">Needs Attention</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">TempDB Setup</span>
                <Badge variant="secondary">Reviewed</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Security Settings</span>
                <Badge variant="default">Compliant</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const PerformanceFindings = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Performance Analysis</h2>
          <p className="text-gray-600 mt-1">Query performance, indexing, and resource utilization analysis for {clientCode.toUpperCase()}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Issues</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{diagnosticStats.performance.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-red-600">Critical</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{diagnosticStats.performance.critical}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-orange-600">High</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{diagnosticStats.performance.high}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-yellow-600">Medium</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{diagnosticStats.performance.medium}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Performance Findings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Performance analysis implementation coming soon...</p>
            <p className="text-sm mt-2">Will analyze query performance, missing indexes, and resource bottlenecks</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const SecurityFindings = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Security Assessment</h2>
          <p className="text-gray-600 mt-1">Security vulnerabilities and compliance analysis for {clientCode.toUpperCase()}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Issues</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{diagnosticStats.security.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-red-600">Critical</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{diagnosticStats.security.critical}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-orange-600">High</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{diagnosticStats.security.high}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-yellow-600">Medium</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{diagnosticStats.security.medium}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Security Findings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Security analysis implementation coming soon...</p>
            <p className="text-sm mt-2">Will analyze permissions, authentication, and security vulnerabilities</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Bug className="w-8 h-8 text-shiftleft-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">SQL Server Diagnostics</h1>
              <p className="text-gray-600">Comprehensive database assessment and analysis for {clientCode.toUpperCase()}</p>
            </div>
          </div>
        </div>

        {/* Diagnostics Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="diagnostics-tabs-droppable" direction="horizontal">
              {(provided: any) => (
                <TabsList
                  className="flex gap-1 bg-white p-1 border rounded-lg"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {tabs.map((tab, idx) => (
                    <Draggable key={tab.key} draggableId={tab.key} index={idx}>
                      {(dragProvided: any, dragSnapshot: any) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className={dragSnapshot.isDragging ? 'opacity-60' : ''}
                        >
                          <TabsTrigger
                            value={tab.key}
                            className="data-[state=active]:bg-shiftleft-50 data-[state=active]:border-shiftleft-200"
                          >
                            <TabHeader
                              icon={tab.icon}
                              title={tab.title}
                              stats={tab.stats}
                              tabKey={tab.key}
                              isActive={activeTab === tab.key}
                            />
                          </TabsTrigger>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TabsList>
              )}
            </Droppable>
          </DragDropContext>

          <TabsContent value="bacpac" className="space-y-6">
            <BacpacAssessmentReport />
          </TabsContent>

          <TabsContent value="dacpac" className="space-y-6">
            <DacpacFindings />
          </TabsContent>

          <TabsContent value="querystore" className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-6 h-6 text-shiftleft-600" />
                <h2 className="text-2xl font-bold text-gray-900">Query Store Insights</h2>
              </div>
              <div className="bg-shiftleft-50 border border-shiftleft-200 rounded-lg p-6 text-shiftleft-900">
                <p className="mb-2">Query Store tracks query performance, plan changes, and regressions over time. Use these insights to identify and resolve performance issues.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-shiftleft-700">Top Resource-Consuming Queries</CardTitle></CardHeader>
                    <CardContent>
                      <table className="w-full text-xs">
                        <thead><tr><th className="text-left">Query</th><th>CPU (ms)</th><th>Reads</th></tr></thead>
                        <tbody>
                          <tr><td>SELECT * FROM Orders</td><td>1200</td><td>150000</td></tr>
                          <tr><td>UPDATE Customers</td><td>950</td><td>12000</td></tr>
                          <tr><td>INSERT INTO Sales</td><td>800</td><td>9000</td></tr>
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-purple-700">Regressed Queries</CardTitle></CardHeader>
                    <CardContent>
                      <table className="w-full text-xs">
                        <thead><tr><th className="text-left">Query</th><th>Prev. Duration</th><th>Current Duration</th></tr></thead>
                        <tbody>
                          <tr><td>SELECT * FROM Orders</td><td>200ms</td><td>1200ms</td></tr>
                          <tr><td>UPDATE Customers</td><td>300ms</td><td>950ms</td></tr>
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-orange-700">Plan Changes</CardTitle></CardHeader>
                    <CardContent>
                      <table className="w-full text-xs">
                        <thead><tr><th className="text-left">Query</th><th>Plan Count</th><th>Last Change</th></tr></thead>
                        <tbody>
                          <tr><td>SELECT * FROM Orders</td><td>3</td><td>2025-10-10</td></tr>
                          <tr><td>UPDATE Customers</td><td>2</td><td>2025-10-09</td></tr>
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </div>
                <div className="text-xs text-shiftleft-800">(Mock data shown. Live Query Store integration coming soon.)</div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="dmv" className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Search className="w-6 h-6 text-shiftleft-600" />
                <h2 className="text-2xl font-bold text-gray-900">DMV Explorer</h2>
              </div>
              <div className="bg-shiftleft-50 border border-shiftleft-200 rounded-lg p-6 text-shiftleft-900">
                <p>All SQL Server Dynamic Management Views (DMVs) are grouped by category for easier navigation:</p>
                <div className="mt-4">
                  <Accordion type="multiple" className="w-full">
                    {DMV_CATEGORIES.map((cat) => (
                      <AccordionItem key={cat.category} value={cat.category}>
                        <AccordionTrigger className="text-lg font-bold text-shiftleft-700 bg-shiftleft-100 px-4">{cat.category}</AccordionTrigger>
                        <AccordionContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {cat.dmvs.map((dmv) => (
                              <Card key={dmv.name} className="h-full">
                                <CardHeader className="pb-2">
                                  <CardTitle className="font-mono text-shiftleft-800 text-base">{dmv.name}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <div className="text-sm text-shiftleft-900">{dmv.description}</div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Performance content removed */}

          {/* Security content removed */}
        </Tabs>
      </div>
    </div>
  );
};

export default DiagnosticsPage;